import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../utils/api";
import { ESTADOS_SOLICITUD, Badge, estadoVisible, mapaEstadosSolicitud } from "../utils/estados";
import { Ayuda } from "../context/AyudaContext";
import Cargando from "../components/Cargando";

// Entregada y Rechazada no aparecen aquí — esta bandeja es solo para lo que
// todavía está en curso; una vez que el proceso termina, se archiva y se
// consulta desde Histórico (igual que ya hace el Dashboard).
const FILTROS = [
  { valor: "", texto: "Todas" },
  { valor: "nueva", texto: "Nuevas" },
  // "En cola" (todavía sin cotizaciones) y "Cotizando" son el mismo estado en la base: el servidor los separa
  // con estos filtros, igual que el Dashboard. Antes "Cotizando" mezclaba las dos y "En cola" no existía.
  { valor: "en_cola_solo", clave: "en_cola", texto: "En cola" },
  { valor: "cotizando_solo", clave: "cotizando", texto: "Cotizando" },
  { valor: "pendiente_aprobacion", texto: "Esperando aprobación" },
  { valor: "aprobada", texto: "Aprobadas" },
  { valor: "en_proceso_pago", texto: "Proceso de pago" },
  { valor: "pagada", texto: "Pagadas" },
  { valor: "en_proceso_compra", texto: "Proceso de compra" },
  { valor: "lista_para_retiro", texto: "Lista para retiro" },
];

const TAMANOS_PAGINA = ["10", "20", "50", "todas"];

const PALETA_AVATAR = ["#3d5a99", "var(--tema-primario, #2f6690)", "#2f7a54", "#b8722a", "#a3402f", "#5b6472"];

function iniciales(nombre) {
  if (!nombre) return "?";
  const partes = nombre.trim().split(/\s+/);
  return ((partes[0]?.[0] || "") + (partes[1]?.[0] || "")).toUpperCase();
}

// Un color fijo por persona (según su nombre) en vez del mismo morado para
// todos — ayuda a distinguir de un vistazo quién pidió qué en la tabla.
function colorAvatar(nombre) {
  if (!nombre) return PALETA_AVATAR[0];
  let hash = 0;
  for (let i = 0; i < nombre.length; i++) hash = nombre.charCodeAt(i) + ((hash << 5) - hash);
  return PALETA_AVATAR[Math.abs(hash) % PALETA_AVATAR.length];
}

function BandejaCompras() {
  const usuario = JSON.parse(localStorage.getItem("usuario") || "null");
  const primerNombre = usuario?.nombre?.split(" ")[0] || "";
  const hora = new Date().getHours();
  const saludoHora = hora < 12 ? "Buenos días" : hora < 19 ? "Buenas tardes" : "Buenas noches";

  const [solicitudes, setSolicitudes] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");

  const [tamanoPagina, setTamanoPagina] = useState("10");
  const [pagina, setPagina] = useState(1);

  const cargar = () => {
    setCargando(true);
    // activas=1 deja afuera lo entregado y lo rechazado — esta bandeja es
    // solo para lo que sigue en curso, incluso en "Todas".
    const params = new URLSearchParams({ activas: "1" });
    if (filtro) params.set("estado", filtro);
    apiFetch(`/api/solicitudes?${params.toString()}`)
      .then((data) => setSolicitudes(data?.items || []))
      .catch((err) => setError(err.message))
      .finally(() => setCargando(false));
  };

  useEffect(cargar, [filtro]);

  const cambiarFiltro = (valor) => {
    setFiltro(valor);
    setPagina(1);
  };

  const totalPaginas =
    tamanoPagina === "todas" ? 1 : Math.max(1, Math.ceil(solicitudes.length / Number(tamanoPagina)));
  const paginaSegura = Math.min(pagina, totalPaginas);
  const visibles =
    tamanoPagina === "todas"
      ? solicitudes
      : solicitudes.slice((paginaSegura - 1) * Number(tamanoPagina), paginaSegura * Number(tamanoPagina));

  return (
    <div>
      <h2 style={{ marginBottom: 0, textAlign: "center" }}>🗂️ Bandeja de Compras</h2>
      <Ayuda>
        <p style={{ color: "#5b6472", marginTop: "0.25rem", textAlign: "center" }}>
          Solicitudes todavía en curso, agrupadas por su estado actual. Las entregadas o rechazadas
          se archivan en <Link to="/historico" style={{ color: "var(--tema-primario, #2f6690)", fontWeight: 600 }}>Histórico</Link>.
        </p>
      </Ayuda>

      <div style={styles.card}>
        <div style={styles.pills}>
          {FILTROS.map((f) => {
            const info = f.valor ? mapaEstadosSolicitud()[f.clave || f.valor] : null;
            const activo = filtro === f.valor;
            let estiloColor = {};
            if (info) {
              estiloColor = activo
                ? { background: info.color, borderColor: info.color, color: "#fff" }
                : { background: info.bg, borderColor: info.bg, color: info.color };
            } else if (activo) {
              estiloColor = styles.pillActivo;
            }
            return (
              <button
                key={f.valor}
                type="button"
                onClick={() => cambiarFiltro(f.valor)}
                style={{ ...styles.pill, ...estiloColor }}
              >
                {f.texto}
              </button>
            );
          })}
        </div>

        {error && <p style={{ color: "#a3402f" }}>{error}</p>}

        {cargando ? (
          <Cargando compacto />
        ) : solicitudes.length === 0 ? (
          <div className="vacio-card" style={styles.vacioCard}>
            <div style={styles.vacioIconoWrap}>
              <div style={{ ...styles.vacioGlow, background: filtro ? "rgba(47,102,144,0.16)" : "rgba(47,122,84,0.16)" }} />
              <span
                className="vacio-anillo"
                style={{ ...styles.vacioAnillo, borderColor: filtro ? "var(--tema-primario, #2f6690)" : "#2f7a54" }}
              />
              <div style={{ ...styles.vacioIcono, background: filtro ? "var(--tema-suave, #dceaf5)" : "#dcf0e6", color: filtro ? "var(--tema-primario, #2f6690)" : "#2f7a54" }}>
                {filtro ? "🔍" : "✅"}
              </div>
            </div>
            <p style={styles.vacioEyebrow}>
              {saludoHora}
              {primerNombre ? `, ${primerNombre}` : ""} 👋
            </p>
            <h3 style={styles.vacioTitulo}>{filtro ? "Sin resultados en este filtro" : "¡Todo al día!"}</h3>
            <p style={styles.vacioTexto}>
              {filtro
                ? "No hay ninguna solicitud en curso que coincida con este estado por ahora."
                : "No hay ninguna solicitud en curso — todo lo pendiente ya fue atendido."}
            </p>
            <div style={{ display: "flex", gap: "0.6rem", justifyContent: "center", marginTop: "1.25rem" }}>
              {filtro && (
                <button type="button" className="vacio-refrescar" onClick={() => cambiarFiltro("")} style={styles.vacioBoton}>
                  Ver todas
                </button>
              )}
              <button type="button" className="vacio-refrescar" onClick={cargar} style={styles.vacioBoton}>
                🔄 Actualizar
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="table-scroll" style={styles.tablaWrap}>
              <table style={styles.tabla}>
                <thead>
                  <tr style={{ textAlign: "center", borderBottom: "1px solid #d3d9df" }}>
                    <th style={styles.thAcento}></th>
                    <th style={styles.th}>Folio</th>
                    <th style={styles.th}>Solicitante</th>
                    <th style={styles.th}>Área</th>
                    <th style={styles.th}>Requerimiento</th>
                    <th style={styles.th}>Fecha</th>
                    <th style={styles.th}>Estado</th>
                    <th style={styles.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {visibles.map((s, i) => {
                    const estadoInfo =
                      mapaEstadosSolicitud()[
                        estadoVisible(s.estado, s.total_cotizaciones, s.tiene_pendiente_aprobacion, s.en_atencion, s.tiene_pago_pendiente, s.tiene_pago_realizado)
                      ] || ESTADOS_SOLICITUD.nueva;
                    return (
                    <tr
                      key={s.id}
                      style={{
                        borderBottom: "1px solid #eef1f3",
                        background: i % 2 === 1 ? "#fafbfc" : "transparent",
                      }}
                    >
                      <td style={{ ...styles.tdAcento, background: estadoInfo.color }}></td>
                      <td style={styles.td}>
                        <Link to={`/solicitud/${s.id}`} style={styles.linkFolio}>
                          #{String(s.id).padStart(3, "0")}
                        </Link>
                        {!!s.tiene_urgente && <div style={styles.badgeUrgente}>🔴 Urgente</div>}
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                          <span style={{ ...styles.avatar, background: colorAvatar(s.solicitante_nombre) }}>
                            {iniciales(s.solicitante_nombre)}
                          </span>
                          {s.solicitante_nombre}
                        </div>
                      </td>
                      <td style={styles.td}>{s.area_nombre}</td>
                      <td style={styles.tdRequerimiento} title={s.descripcion_requerimiento}>
                        {s.descripcion_requerimiento}
                      </td>
                      <td style={styles.td}>{new Date(s.creado_en).toLocaleDateString("es-NI")}</td>
                      <td style={styles.td}>
                        <Badge
                          estado={estadoVisible(s.estado, s.total_cotizaciones, s.tiene_pendiente_aprobacion, s.en_atencion, s.tiene_pago_pendiente, s.tiene_pago_realizado)}
                          mapa={mapaEstadosSolicitud()}
                        />
                      </td>
                      <td style={styles.td}>
                        <Link to={`/solicitud/${s.id}`} style={styles.botonVer}>
                          Ver detalle
                        </Link>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={styles.pieTabla}>
              <span style={{ fontSize: "0.8rem", color: "#5b6472" }}>
                Mostrando {(paginaSegura - 1) * (tamanoPagina === "todas" ? solicitudes.length : Number(tamanoPagina)) + 1}
                {" a "}
                {Math.min(
                  paginaSegura * (tamanoPagina === "todas" ? solicitudes.length : Number(tamanoPagina)),
                  solicitudes.length
                )}
                {" de "}
                {solicitudes.length}
              </span>

              <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                {totalPaginas > 1 && (
                  <div style={{ display: "flex", gap: "0.3rem" }}>
                    <button
                      type="button"
                      disabled={paginaSegura === 1}
                      onClick={() => setPagina(paginaSegura - 1)}
                      style={styles.botonPagina}
                    >
                      ‹
                    </button>
                    {Array.from({ length: totalPaginas }, (_, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setPagina(i + 1)}
                        style={{ ...styles.botonPagina, ...(paginaSegura === i + 1 ? styles.botonPaginaActivo : {}) }}
                      >
                        {i + 1}
                      </button>
                    ))}
                    <button
                      type="button"
                      disabled={paginaSegura === totalPaginas}
                      onClick={() => setPagina(paginaSegura + 1)}
                      style={styles.botonPagina}
                    >
                      ›
                    </button>
                  </div>
                )}
                <label style={{ fontSize: "0.8rem", color: "#5b6472", display: "flex", alignItems: "center", gap: "0.4rem" }}>
                  Por página:
                  <select
                    value={tamanoPagina}
                    onChange={(e) => {
                      setTamanoPagina(e.target.value);
                      setPagina(1);
                    }}
                    style={{ ...styles.input, padding: "0.3rem 0.4rem" }}
                  >
                    {TAMANOS_PAGINA.map((t) => (
                      <option key={t} value={t}>
                        {t === "todas" ? "Todas" : t}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  card: {
    border: "1px solid #e2e6ea",
    borderRadius: 14,
    padding: "1.25rem 1.4rem",
    marginTop: "1.1rem",
    background: "#fff",
    boxShadow: "0 1px 3px rgba(27,42,74,0.06)",
  },
  vacioCard: {
    marginTop: "0.5rem",
    borderRadius: 12,
    padding: "3rem 1.5rem 2.5rem",
    background: "linear-gradient(180deg, #f7f9fc 0%, #fff 55%)",
    textAlign: "center",
  },
  vacioIconoWrap: {
    position: "relative",
    width: 96,
    height: 96,
    margin: "0 auto 0.9rem",
  },
  vacioGlow: {
    position: "absolute",
    inset: -10,
    borderRadius: "50%",
  },
  vacioAnillo: {
    position: "absolute",
    inset: 16,
    borderRadius: "50%",
    border: "2px solid",
  },
  vacioIcono: {
    position: "absolute",
    inset: 16,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.8rem",
  },
  vacioEyebrow: {
    margin: "0 0 0.25rem",
    color: "#2f7a54",
    fontSize: "0.82rem",
    fontWeight: 700,
  },
  vacioTitulo: {
    margin: "0 0 0.4rem",
    color: "var(--tema-titulo, #0b3c60)",
    fontSize: "1.25rem",
  },
  vacioTexto: {
    margin: "0 auto",
    maxWidth: 380,
    color: "#5b6472",
    fontSize: "0.88rem",
    lineHeight: 1.5,
  },
  vacioBoton: {
    background: "#fff",
    border: "1px solid #d3d9df",
    borderRadius: 8,
    padding: "0.5rem 1.1rem",
    fontSize: "0.82rem",
    fontWeight: 600,
    color: "var(--tema-titulo, #0b3c60)",
    cursor: "pointer",
  },
  pills: {
    display: "flex",
    gap: "0.5rem",
    flexWrap: "wrap",
    marginBottom: "1.1rem",
  },
  pill: {
    border: "1px solid #d3d9df",
    background: "#fff",
    color: "#5b6472",
    borderRadius: 999,
    padding: "0.4rem 0.9rem",
    fontSize: "0.82rem",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  pillActivo: {
    background: "var(--tema-titulo, #0b3c60)",
    borderColor: "var(--tema-titulo, #0b3c60)",
    color: "#fff",
  },
  tablaWrap: {
    border: "1px solid #eef1f3",
    borderRadius: 10,
  },
  tabla: { width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" },
  th: {
    padding: "0.6rem 0.5rem",
    background: "#eef1f3",
    border: "1px solid #d3d9df",
    fontSize: "0.78rem",
    color: "var(--tema-titulo, #0b3c60)",
  },
  thAcento: {
    padding: 0,
    width: 4,
    background: "#eef1f3",
    border: "none",
    borderBottom: "1px solid #d3d9df",
  },
  tdAcento: { padding: 0, width: 4 },
  td: { padding: "0.55rem 0.5rem", textAlign: "left" },
  tdRequerimiento: {
    padding: "0.55rem 0.5rem",
    maxWidth: 280,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    textAlign: "left",
  },
  linkFolio: { color: "var(--tema-primario, #2f6690)", fontWeight: 600, textDecoration: "none" },
  badgeUrgente: {
    display: "inline-block",
    marginTop: "0.15rem",
    fontSize: "0.68rem",
    fontWeight: 700,
    color: "#a3402f",
    background: "#f7ded9",
    borderRadius: 999,
    padding: "0.05rem 0.5rem",
    whiteSpace: "nowrap",
  },
  botonVer: {
    display: "inline-block",
    border: "1px solid #b8d2e8",
    borderRadius: 8,
    padding: "0.35rem 0.8rem",
    fontSize: "0.8rem",
    fontWeight: 600,
    color: "var(--tema-primario, #2f6690)",
    background: "var(--tema-suave, #eef4fb)",
    textDecoration: "none",
    whiteSpace: "nowrap",
  },
  avatar: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    background: "#3d5a99",
    color: "#fff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.68rem",
    fontWeight: 700,
    flexShrink: 0,
  },
  input: {
    padding: "0.5rem",
    border: "1px solid #d3d9df",
    borderRadius: 6,
    fontFamily: "inherit",
    fontSize: "0.85rem",
  },
  pieTabla: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "0.75rem",
    marginTop: "0.9rem",
  },
  botonPagina: {
    border: "1px solid #d3d9df",
    background: "#fff",
    color: "var(--tema-titulo, #0b3c60)",
    borderRadius: 6,
    padding: "0.3rem 0.6rem",
    fontSize: "0.8rem",
    fontWeight: 600,
    cursor: "pointer",
    minWidth: 30,
  },
  botonPaginaActivo: {
    background: "var(--tema-titulo, #0b3c60)",
    borderColor: "var(--tema-titulo, #0b3c60)",
    color: "#fff",
  },
};

export default BandejaCompras;
